import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import React from 'react';
import { Modal, ModalHeader, ModalBody, Label, Input } from 'reactstrap';
import FileBase64 from 'react-file-base64';
import { Wizard, Steps, Step } from 'react-albus';
import { v4 as uuidv4 } from 'uuid';
import Select from 'react-select';
import * as jsPDF from 'jspdf';

import DragAndDropFileUpload from '../../DragDropUpload/index';
import { ButtonGroup } from 'reactstrap';
import { Button } from 'reactstrap';

import {
    HrFileCategory,
    SelectOption,
    IStep,
    IUser,
    PushStep,
    hrFileCategories,
    IFile,
} from '../../../globals/interfaces';
import TopNavigation from '../../wizard-hooks/TopNavigation';
import Webcam from 'react-webcam';
import CropPhoto from '../../counter/CropPhoto';
import _ from 'lodash';
import moment from 'moment';
import apiClient from '../../../apiClient';
import { FormGroup } from 'reactstrap';
import { FileTypesMap, PostSave } from '../../hr/Files';
import Switch from 'rc-switch';
import ActionIcon from '../ActionIcon';
import { Alert } from 'reactstrap';

type Process = 'UPLOAD' | 'SCAN';

const defaultFile = {
    base64: '',
    type: '',
    guid: '',
};

const defaultOption = {
    label: '',
    value: '',
};

interface Props<T> {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    user: IUser;
    s_container: string;
    s_type: string;
    requiredName?: string;
    s_transaction_id: string;
    s_mawb_id?: string;
    fileTypesMap: FileTypesMap;
    expiration_date?: string;
    set_expiration_date?: React.Dispatch<React.SetStateAction<string>>;
    postSave?: PostSave;
    additionalDisabled?: boolean;
    requiredFileType?: 'png' | 'jpg' | 'jpeg';
    multiple?: boolean;
}

type UseHrFileCategory = HrFileCategory | '';


// Helper function to get file size from base64 string
const getBase64FileSize = (base64String: string): number => {
    // Remove data URL prefix if present
    const base64Data = base64String.split(',')[1] || base64String;
    // Calculate size in bytes
    const padding = base64Data.endsWith('==') ? 2 : base64Data.endsWith('=') ? 1 : 0;
    return (base64Data.length * 3) / 4 - padding;
};

// Helper function to format bytes to MB
const formatBytes = (bytes: number): string => {
    return (bytes / (1024 * 1024)).toFixed(2);
};

// Image compression function
const compressImage = (
    base64String: string,
    maxSizeInBytes: number,
    fileType: string
): Promise<{ base64: string; size: number; compressed: boolean }> => {
    return new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('Could not get canvas context'));
                return;
            }

            // Start with original dimensions
            let width = img.width;
            let height = img.height;

            // Maximum dimension to prevent memory issues
            const MAX_DIMENSION = 4096;

            // Scale down if image is too large
            if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
                if (width > height) {
                    height = (height / width) * MAX_DIMENSION;
                    width = MAX_DIMENSION;
                } else {
                    width = (width / height) * MAX_DIMENSION;
                    height = MAX_DIMENSION;
                }
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            // Determine mime type
            const mimeType = fileType.toLowerCase().includes('png')
                ? 'image/png'
                : 'image/jpeg';

            // Try different quality levels
            let quality = 0.9;
            let compressed = false;

            const tryCompress = () => {
                const compressedBase64 = canvas.toDataURL(mimeType, quality);
                const compressedSize = getBase64FileSize(compressedBase64);

                if (compressedSize <= maxSizeInBytes || quality <= 0.1) {
                    // Return the result
                    resolve({
                        base64: compressedBase64,
                        size: compressedSize,
                        compressed: compressedBase64 !== base64String,
                    });
                } else {
                    // Try lower quality
                    quality -= 0.1;
                    compressed = true;
                    tryCompress();
                }
            };

            tryCompress();
        };

        img.onerror = () => {
            reject(new Error('Failed to load image'));
        };

        img.src = base64String;
    });
};

export default function UploadFiles<T>({
    open,
    setOpen,
    requiredName,
    user,
    s_container,
    s_type,
    s_transaction_id,
    s_mawb_id,
    fileTypesMap,
    expiration_date,
    set_expiration_date,
    postSave,
    additionalDisabled,
    requiredFileType,
}: Props<T>) {
    const [types, setTypes] = useState<Array<SelectOption>>([]);
    const [s_file_type, set_s_file_type] = useState('');
    const [process, setProcess] = useState<Process>('UPLOAD');
    const processOptions: Array<{ name: string; type: Process }> = [
        {
            name: 'Upload',
            type: 'UPLOAD',
        },
        {
            name: 'Scan',
            type: 'SCAN',
        },
    ];
    const [file, setFile] = useState<IFile>(defaultFile);
    const [files, setFiles] = useState<Array<IFile>>([]);
    const [selectedFileOption, setSelectedFileOption] =
        useState<SelectOption>(defaultOption);
    const [multiple, setMultiple] = useState(false);
    const [category, setCategory] = useState<UseHrFileCategory>('ID');
    const [saving, setSaving] = useState(false);
    const webcamRef = React.useRef(null);

    // New state for file size validation
    const [fileSizeError, setFileSizeError] = useState<string>('');
    const [isCompressing, setIsCompressing] = useState(false);

    const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB in bytes


    const handleSelectProcess = (process: Process) => {
        setProcess(process);
        setFile(defaultFile);
        setFileSizeError('');
    };

    const toggle = () => {
        setOpen((prev) => !prev);
        setFileSizeError('');
    };

    useEffect(() => {
        setFile(defaultFile);
        setFileSizeError('');
    }, [open]);

    useEffect(() => {
        const types: Array<SelectOption> = [];
        for (const type in fileTypesMap) {
            if (category && fileTypesMap[type].category === category) {
                types.push({
                    label: type,
                    value: type,
                });
            } else if (!category) {
                types.push({
                    label: type,
                    value: type,
                });
            }
        }
        types.sort((a, b) => a.label.localeCompare(b.label));
        setTypes(types);
    }, [fileTypesMap, category]);

    const [deviceId, setDeviceId] = React.useState({});
    const [devices, setDevices] = React.useState<MediaDeviceInfo[]>([]);

    useEffect(() => {
        if (devices.length > 0) {
            setDeviceId(devices[0].deviceId);
        }
    }, [devices]);

    useEffect(() => {
        function handleDevices(mediaDevices: MediaDeviceInfo[]) {
            const filteredDevices = mediaDevices.filter(
                ({ kind, deviceId }) =>
                    kind === 'videoinput' && deviceId.length > 0
            );
            setDevices(filteredDevices);
        }
        navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }, []);

    const topNavClick = (stepItem: IStep, push: PushStep) => {
        const { id } = stepItem;
        if (id === '3' && file.base64.length) {
            push(id);
        } else if (id !== '3') {
            push(id);
        }
    };

    const [rotated, setRotated] = useState(false);
    const [upImg, setUpImg] = useState();
    const imgRef = useRef(null);

    const handleSelectOption = (option: SelectOption) => {
        setSelectedFileOption(option);
        set_s_file_type(String(option.value));
    };

    const capture = (next: () => void) => {
        if (!webcamRef || !webcamRef.current) return;
        // @ts-ignore
        const imageSrc = webcamRef.current.getScreenshot();

        let img = document.createElement('img');
        img.setAttribute('src', imageSrc);

        // @ts-ignore
        imgRef.current = img;
        setUpImg(imageSrc);

        next();
    };

    const updateFile = async (prop: string, value: any): Promise<void> => {
        const copy: any = _.cloneDeep(file);
        copy[prop] = value;
        if (!copy.type) {
            copy.type = requiredFileType || 'png';
        }

        // If updating base64 and it's an image, compress it
        if (prop === 'base64' && value && copy.type && copy.type.toLowerCase().includes('image')) {
            setIsCompressing(true);
            setFileSizeError('');

            try {
                const originalSize = getBase64FileSize(value);

                // Only compress if original size is > 2MB
                if (originalSize > MAX_FILE_SIZE) {
                    const result = await compressImage(value, MAX_FILE_SIZE, copy.type);

                    if (result.size > MAX_FILE_SIZE) {
                        // Still too large after compression
                        setFileSizeError(
                            `File size is ${formatBytes(result.size)} MB after compression. Maximum allowed size is 2 MB. Please use a smaller image or reduce the image dimensions.`
                        );
                        setIsCompressing(false);
                        return; // Don't update the file
                    }

                    copy.base64 = result.base64;
                    console.log(`Image compressed from ${formatBytes(originalSize)} MB to ${formatBytes(result.size)} MB`);
                } else {
                    console.log(`Image size (${formatBytes(originalSize)} MB) is within limits, no compression needed`);
                }
            } catch (error) {
                console.error('Error compressing image:', error);
                setFileSizeError('Error processing image. Please try a different file.');
                setIsCompressing(false);
                return;
            }

            setIsCompressing(false);
        }

        setFile(copy);
    };

    // Enhanced setFile wrapper with validation
    const setFileWithValidation = async (newFile: IFile) => {
        // Check if it's an image file (not PDF)
        const isImage = newFile.type && newFile.type.toLowerCase().includes('image');
        const isPdf = newFile.type && newFile.type.toLowerCase().includes('pdf');

        if (isImage && newFile.base64) {
            setIsCompressing(true);
            setFileSizeError('');

            try {
                const originalSize = getBase64FileSize(newFile.base64);

                // Only compress if original size is > 2MB
                if (originalSize > MAX_FILE_SIZE) {
                    const result = await compressImage(newFile.base64, MAX_FILE_SIZE, newFile.type);

                    if (result.size > MAX_FILE_SIZE) {
                        // Still too large after compression
                        setFileSizeError(
                            `File size is ${formatBytes(result.size)} MB after compression. Maximum allowed size is 2 MB. Please use a smaller image or reduce the image dimensions.`
                        );
                        setIsCompressing(false);
                        return; // Don't set the file
                    }

                    newFile.base64 = result.base64;
                    console.log(`Image compressed from ${formatBytes(originalSize)} MB to ${formatBytes(result.size)} MB`);
                } else {
                    console.log(`Image size (${formatBytes(originalSize)} MB) is within limits, no compression needed`);
                }
            } catch (error) {
                console.error('Error compressing image:', error);
                setFileSizeError('Error processing image. Please try a different file.');
                setIsCompressing(false);
                return;
            }

            setIsCompressing(false);
        }

        setFile(newFile);
    };

    const retakePhoto = (previous: () => void) => {
        previous();
    };

    const getImageDimensions = (imgData: any) => {
        return new Promise((resolve, reject) => {
            const image = new Image();
            image.onload = function () {
                // @ts-ignore
                URL.revokeObjectURL(this.src); // Important: release object URL to avoid memory leaks
                resolve({
                    // @ts-ignore
                    imgWidthPx: this.naturalWidth, imgHeightPx: this.naturalHeight,
                });
            };
            image.onerror = function () {
                reject(new Error('Failed to load image'));
            };

            // Convert base64 to blob
            const byteCharacters = atob(imgData);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'image/jpeg' });

            // Create an object URL for the blob
            const blobUrl = URL.createObjectURL(blob);

            image.src = blobUrl;
        });
    };

    const handleSave = async () => {
        setSaving(true);
        let useFile = file;

        if (multiple) {
            const doc = new jsPDF('p', 'mm', 'a4');
            // size of margins (in mm)
            const margin = 10;
            const marginPt = (margin * 72) / 25.4; // Convert mm to points
            const dpi = 300;

            for (let index = 0; index < files.length; index++) {
                const file = files[index];
                const imgData = file.base64.split(',')[1]; // Remove the data:image/png;base64, part
                if (index !== 0) {
                    doc.addPage(); // Add new page for each image after the first one
                }
                // @ts-ignore
                const { imgWidthPx, imgHeightPx } = await getImageDimensions(
                    imgData
                );

                const imgWidthIn = imgWidthPx / dpi;
                const imgHeightIn = imgHeightPx / dpi;

                // convert inches to points
                const imgWidthPt = imgWidthIn * 72 - marginPt;
                const imgHeightPt = (imgWidthPt / imgWidthIn) * imgHeightIn;

                // calculate the width and height of the image in the PDF (in mm)
                let imgWidthMm = imgWidthPx / 28.35; // roughly converting pixels to mm
                let imgHeightMm = imgHeightPx / 28.35; // roughly converting pixels to mm

                doc.addImage(
                    imgData,
                    'JPEG',
                    margin,
                    margin,
                    imgWidthPt,
                    imgHeightPt
                );
            }

            // Output as base64
            const base64String = doc.output('datauristring');

            // Remove the data:application/pdf;base64, part
            const base64Data = base64String.split(',')[1];

            useFile.base64 = base64Data;
            useFile.type = 'pdf';
        }

        const fileData = {
            file: useFile,
            requiredName: requiredName || null,
            s_transaction_id,
            s_mawb_id: s_mawb_id || uuidv4(),
            s_container,
            s_type,
            s_file_type,
            s_created_by: user.s_email,
            t_created: moment().format('MM/DD/YYYY HH:mm'),
        };
        const res = await apiClient.post('/addFile', fileData);
        let expirationReminder;
        if (fileTypesMap[selectedFileOption.value]) {
            expirationReminder =
                fileTypesMap[selectedFileOption.value].expirationReminder;
        }
        if (res.status === 200) {
            postSave &&
                (await postSave(
                    res.data,
                    expirationReminder,
                    category,
                    s_file_type
                ));
            setFile(defaultFile);
            setFiles([]);
            setSelectedFileOption(defaultOption);
            toggle();
        }
        setSaving(false);
    };

    const handleSelectCategory = (_category: UseHrFileCategory) => {
        if (category === _category) {
            setCategory('');
        } else {
            setCategory(_category);
        }
    };

    const expirationRequired = Boolean(
        _.get(fileTypesMap, `${selectedFileOption.value}.expires`)
    );
    const expirationSelected =
        !expirationRequired || (expirationRequired && Boolean(expiration_date));
    const base =
        additionalDisabled === true ||
        !selectedFileOption.value ||
        !expirationSelected;
    const disabled = !file.base64.length || base;
    const disabledMultiple = !files.length || base;

    return (
        <CustomModal isOpen={open} toggle={toggle}>
            <ModalHeader>Upload Files</ModalHeader>
            <ModalBody>
                <div>
                    {fileSizeError && (
                        <Alert color="danger" className="mb-3">
                            {fileSizeError}
                        </Alert>
                    )}
                    {isCompressing && (
                        <Alert color="info" className="mb-3">
                            Compressing image, please wait...
                        </Alert>
                    )}
                    <FlexContainer>
                        <div>
                            <Label className={'d-block'}>Category: </Label>
                            <ButtonGroup>
                                {hrFileCategories.map((option) => (
                                    <Button
                                        onClick={() =>
                                            handleSelectCategory(option)
                                        }
                                        active={option === category}
                                    >
                                        {option}
                                    </Button>
                                ))}
                            </ButtonGroup>
                        </div>
                        <SelectContainer>
                            <Label>File Type:</Label>
                            <Select
                                value={selectedFileOption}
                                options={types}
                                onChange={(option: SelectOption) =>
                                    handleSelectOption(option)
                                }
                            />
                        </SelectContainer>
                        <FlexContainer>
                            <div>
                                <Label className={'d-block'}>
                                    Upload Type:{' '}
                                </Label>
                                <ButtonGroup>
                                    {processOptions.map((option) => (
                                        <Button
                                            onClick={() =>
                                                handleSelectProcess(option.type)
                                            }
                                            active={option.type === process}
                                        >
                                            {option.name}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </div>
                            {process === 'SCAN' && (
                                <div>
                                    <Label className={'mr-2 d-block'}>
                                        Multiple Pages (PDF)
                                    </Label>
                                    <Switch
                                        checked={multiple}
                                        onChange={() =>
                                            setMultiple((prev) => !prev)
                                        }
                                    />
                                </div>
                            )}
                        </FlexContainer>
                        {expiration_date !== undefined &&
                            set_expiration_date &&
                            _.get(
                                fileTypesMap,
                                `${selectedFileOption.value}.expires`
                            ) && (
                                <FormGroup>
                                    <Label>Expiration Date</Label>
                                    <Input
                                        type={'date'}
                                        value={expiration_date}
                                        onChange={(e: any) =>
                                            set_expiration_date(e.target.value)
                                        }
                                    />
                                </FormGroup>
                            )}
                    </FlexContainer>
                    {process === 'SCAN' ? (
                        <Wizard
                            render={({
                                next,
                                previous,
                                push,
                            }: // @ts-ignore
                                WizardProps) => (
                                <div className="wizard wizard-default mt-1 text-center">
                                    <TopNavigation
                                        className="justify-content-center"
                                        disableNav={false}
                                        topNavClick={topNavClick}
                                    />
                                    <Steps>
                                        <Step id={'1'} name={'Scan'}>
                                            <div className="mb-2">
                                                {devices.map((device, key) => (
                                                    <Button
                                                        key={device.deviceId}
                                                        onClick={() =>
                                                            setDeviceId(
                                                                device.deviceId
                                                            )
                                                        }
                                                        active={
                                                            deviceId ===
                                                            device.deviceId
                                                        }
                                                        className="mr-2"
                                                    >
                                                        {device.label ||
                                                            `Device ${key + 1}`}
                                                    </Button>
                                                ))}
                                            </div>
                                            <Webcam
                                                audio={false}
                                                ref={webcamRef}
                                                screenshotFormat="image/jpeg"
                                                videoConstraints={{
                                                    deviceId,
                                                    width: 2592,
                                                    height: 1944,
                                                }}
                                                width={1280}
                                                height={720}
                                            />
                                            <Button
                                                onClick={() => capture(next)}
                                            >
                                                Next
                                            </Button>
                                        </Step>
                                        <Step id={'2'} name={'Crop'}>
                                            <CropPhoto
                                                upImg={upImg}
                                                setUpImg={setUpImg}
                                                imgRef={imgRef}
                                                retakePhoto={retakePhoto}
                                                next={next}
                                                previous={previous}
                                                updateFile={updateFile}
                                                rotated={rotated}
                                                setRotated={setRotated}
                                                mobile={false}
                                                fileType={s_file_type}
                                            />
                                        </Step>
                                        <Step id={'3'} name={'Save'}>
                                            <img
                                                src={file ? file.base64 : ''}
                                                style={{
                                                    maxWidth: 1000,
                                                    height: 'auto',
                                                }}
                                                alt={'user'}
                                            />
                                            {multiple && (
                                                <div className={'mt-4'}>
                                                    <h6>
                                                        Previously Scanned Files
                                                    </h6>
                                                    <FlexContainer>
                                                        {files
                                                            .slice(
                                                                0,
                                                                files.length - 1
                                                            )
                                                            .map((file, i) => (
                                                                <div
                                                                    className={
                                                                        'text-center'
                                                                    }
                                                                >
                                                                    <h6>
                                                                        {i + 1}
                                                                    </h6>
                                                                    <img
                                                                        src={
                                                                            file.base64
                                                                        }
                                                                        style={{
                                                                            maxWidth: 100,
                                                                            height: 'auto',
                                                                        }}
                                                                        alt={
                                                                            'img'
                                                                        }
                                                                    />
                                                                </div>
                                                            ))}
                                                    </FlexContainer>
                                                </div>
                                            )}
                                            <SaveButtonContainer>
                                                <Button onClick={previous}>
                                                    Back
                                                </Button>
                                                {multiple ? (
                                                    <div>
                                                        <Button
                                                            onClick={() =>
                                                                push('1')
                                                            }
                                                            className={'mr-2'}
                                                        >
                                                            Scan another page
                                                        </Button>
                                                        <ActionIcon
                                                            disabled={
                                                                disabledMultiple
                                                            }
                                                            onClick={handleSave}
                                                            type={'save'}
                                                            loading={saving}
                                                        />
                                                    </div>
                                                ) : (
                                                    <ActionIcon
                                                        disabled={disabled}
                                                        onClick={handleSave}
                                                        type={'save'}
                                                        loading={saving}
                                                    />
                                                )}
                                            </SaveButtonContainer>
                                        </Step>
                                    </Steps>
                                </div>
                            )}
                        />
                    ) : (
                        <div className={'text-center'}>
                            <UploadFileContainer>
                                <DragAndDropFileUpload
                                    onDone={setFileWithValidation}
                                    autoCrop={true}
                                    cropThreshold={230}      // Optional: Adjust whitespace detection sensitivity (default: 240)
                                    cropPadding={15}
                                />
                            </UploadFileContainer>
                            {file.base64 && file.type.includes('image') && (
                                <PreviewContainer>
                                    <h6>Preview:</h6>
                                    <img
                                        src={file.base64}
                                        style={{
                                            maxWidth: 150,
                                            height: 'auto',
                                        }}
                                        alt={'img'}
                                    />
                                    {file.base64 && (
                                        <div className={`mt-2 text-muted small ${fileSizeError ? 'bg-danger' : ''}`}>
                                            {
                                                fileSizeError ? 'File Size Over Limit' : 
                                                `File size: ${formatBytes(getBase64FileSize(file.base64))} MB`
                                            }
                                        </div>
                                    )}
                                </PreviewContainer>
                            )}
                            <SaveButtonContainer>
                                <ActionIcon
                                    disabled={disabled}
                                    onClick={handleSave}
                                    type={'save'}
                                    loading={saving}
                                />
                            </SaveButtonContainer>
                        </div>
                    )}
                </div>
            </ModalBody>
        </CustomModal>
    );
}


const CustomModal = styled(Modal)`
    max-width: 1350px;
    width: 100%;
`;

const SelectContainer = styled.div`
    width: 300px;
`;

const UploadFileContainer = styled.div`
    margin-top: 30px;
    display: flex;
    justify-content: center;
    margin-bottom: 10px;
`;

const FlexContainer = styled.div`
    display: flex;
    gap: 25px;
    justify-content: center;
`;

const SaveButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 10px;
    gap: 10px;
`;

const PreviewContainer = styled.div`
    margin: 10px 0px;
`;